<script lang="ts" context="module">
    export type RingSpec =
        | { kind: 'score'; value: number; max?: number }
        | { kind: 'word'; text: string }
        | { kind: 'percent'; value: number }
        | { kind: 'dots' }
        | { kind: 'icon'; name: string };

    export type RingTone = 'success' | 'warn' | 'danger' | 'info' | 'progress' | 'neutral';
    export type StatusTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';
    export type AgentKey = 'gapfinder' | 'illuminator' | 'brief';

    export const AGENT_META: Record<AgentKey, { name: string; icon: string; accent: string }> = {
        gapfinder: { name: 'GapFinder', icon: 'search', accent: 'pink' },
        illuminator: { name: 'Illuminator', icon: 'lightbulb outline', accent: 'teal' },
        brief: { name: 'Built-to-Brief', icon: 'pencil alternate', accent: 'orange' },
    };
</script>

<script lang="ts">
    import { link, push } from 'svelte-spa-router';
    import StatusBadge from './StatusBadge.svelte';

    export let agent: AgentKey;
    export let href: string;
    export let ariaLabel: string = '';
    export let title: string;
    export let subtitle: string = '';
    export let ring: RingSpec;
    export let ringTone: RingTone = 'neutral';
    export let statusLabel: string = '';
    export let statusTone: StatusTone = 'neutral';
    export let statusDetail: string = '';
    export let refId: string = '';
    export let versionChip: string = '';
    export let footerNote: string = '';
    /** If set, shows a small “Resume” control (e.g. open draft editor without visiting the view). */
    export let resumeHref: string = '';

    $: meta = AGENT_META[agent];

    function handleResume(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (resumeHref) push(resumeHref);
    }

    function ringWordSize(text: string): string {
        const n = (text || '').length;
        if (n <= 3) return '1.5rem';
        if (n <= 4) return '1.2rem';
        if (n <= 6) return '.9rem';
        return '.72rem';
    }
</script>

<a
    use:link
    {href}
    class="tile tone-{ringTone}"
    data-agent={agent}
    aria-label={ariaLabel || `Open ${title}`}
>
    <header class="tile-header">
        <span class="agent-chip">
            <i class="{meta.icon} icon" aria-hidden="true"></i>
            <span class="agent-name">{meta.name}</span>
        </span>
        <span class="header-right">
            {#if refId}<span class="ref">{refId}</span>{/if}
            {#if versionChip}<span class="version-chip">{versionChip}</span>{/if}
        </span>
    </header>

    <div class="tile-body">
        <div class="ring" aria-hidden="true">
            {#if ring.kind === 'score'}
                <span class="ring-number">{Math.round(ring.value)}</span>
                <span class="ring-sub">/{ring.max ?? 100}</span>
            {:else if ring.kind === 'percent'}
                <span class="ring-number">{Math.round(ring.value)}<span class="ring-pct">%</span></span>
            {:else if ring.kind === 'word'}
                <span class="ring-word" style="font-size: {ringWordSize(ring.text)};">{ring.text}</span>
            {:else if ring.kind === 'dots'}
                <span class="ring-dots"><span></span><span></span><span></span></span>
            {:else if ring.kind === 'icon'}
                <i class="{ring.name} icon ring-icon" aria-hidden="true"></i>
            {/if}
        </div>

        <div class="body-text">
            <h3 class="title">{title}</h3>
            {#if subtitle}<p class="subtitle">{subtitle}</p>{/if}
        </div>
    </div>

    <div class="tile-status">
        {#if statusLabel}
            <StatusBadge label={statusLabel} tone={statusTone} />
        {/if}
        {#if statusDetail}<span class="status-detail">{statusDetail}</span>{/if}
    </div>

    {#if footerNote || resumeHref}
        <footer class="tile-footer">
            {#if footerNote}<span class="footer-note">{footerNote}</span>{/if}
            {#if resumeHref}
                <button
                    type="button"
                    class="resume-btn"
                    aria-label="Resume draft"
                    on:click={handleResume}
                >
                    <i class="pencil alternate icon" aria-hidden="true"></i>
                    Resume
                </button>
            {/if}
        </footer>
    {/if}
</a>

<style>
    .tile {
        --accent-base: var(--accent-pink);
        --accent-soft: var(--accent-pink-soft);
        --accent-ink: var(--accent-pink-ink);
        --ring-color: var(--border-strong);
        --ring-text: var(--grey);

        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: var(--space-4) var(--space-5);
        text-decoration: none;
        color: var(--grey);
        transition: border-color .15s, box-shadow .15s, transform .15s;
        min-height: 240px;
        position: relative;
    }

    .tile[data-agent="gapfinder"] {
        --accent-base: var(--accent-pink);
        --accent-soft: var(--accent-pink-soft);
        --accent-ink: var(--accent-pink-ink);
    }
    .tile[data-agent="illuminator"] {
        --accent-base: var(--accent-teal);
        --accent-soft: var(--accent-teal-soft);
        --accent-ink: var(--accent-teal-ink);
    }
    .tile[data-agent="brief"] {
        --accent-base: var(--accent-orange);
        --accent-soft: var(--accent-orange-soft);
        --accent-ink: var(--accent-orange-ink);
    }

    .tile.tone-success { --ring-color: var(--status-success); --ring-text: var(--status-success-ink); --ring-fill: var(--status-success-soft); }
    .tile.tone-warn    { --ring-color: var(--status-warn);    --ring-text: var(--status-warn-ink);    --ring-fill: var(--status-warn-soft); }
    .tile.tone-danger  { --ring-color: var(--status-danger);  --ring-text: var(--status-danger-ink);  --ring-fill: var(--status-danger-soft); }
    .tile.tone-info    { --ring-color: var(--status-info);    --ring-text: var(--status-info-ink);    --ring-fill: var(--status-info-soft); }
    .tile.tone-progress { --ring-color: var(--primary);       --ring-text: var(--primary);            --ring-fill: var(--surface); }
    .tile.tone-neutral { --ring-color: var(--border-strong);  --ring-text: var(--grey-secondary);     --ring-fill: var(--surface); }

    a.tile:hover { border-color: var(--accent-base); }
    a.tile:focus-visible { outline: none; box-shadow: var(--focus-ring); border-color: var(--accent-base); }

    .tile-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
    }

    .agent-chip {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        color: var(--accent-ink);
        font-weight: 500;
        font-size: var(--text-sm);
    }
    .agent-chip :global(i.icon) { margin: 0; color: var(--accent-base); font-size: 1rem; }

    .header-right {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .ref {
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        font-variant-numeric: tabular-nums;
        letter-spacing: .02em;
    }

    .version-chip {
        display: inline-flex;
        align-items: center;
        padding: .1rem .5rem;
        border-radius: var(--radius-pill);
        background: var(--accent-soft);
        color: var(--accent-ink);
        font-size: var(--text-xs);
        font-weight: 600;
        letter-spacing: .02em;
    }

    .tile-body {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        min-width: 0;
        flex: 1;
    }

    .ring {
        flex-shrink: 0;
        width: 72px; height: 72px;
        border-radius: 50%;
        border: 4px solid var(--ring-color);
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        color: var(--ring-text);
        background: var(--ring-fill, var(--surface));
        transition: border-color .2s, background .2s;
    }
    .tile.tone-progress .ring {
        animation: ring-pulse 1.6s ease-in-out infinite;
    }
    .tile.tone-neutral .ring {
        border-style: dashed;
    }
    @keyframes ring-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
        50% { box-shadow: 0 0 0 4px rgba(37, 99, 235, .15); }
    }

    .ring-number {
        font-size: 1.4rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        line-height: 1;
        color: var(--ring-text);
    }
    .ring-sub {
        font-size: .7rem;
        color: var(--grey-secondary);
        font-variant-numeric: tabular-nums;
        margin-top: 2px;
    }
    .ring-pct { font-size: .7rem; font-weight: 500; margin-left: 1px; }

    .ring-word {
        font-weight: 700;
        letter-spacing: .05em;
        text-transform: uppercase;
        line-height: 1;
        color: var(--ring-text);
        text-align: center;
        padding: 0 .2rem;
    }
    .ring-icon {
        font-size: 1.6rem !important;
        color: var(--ring-text) !important;
        margin: 0 !important;
    }

    .ring-dots { display: inline-flex; gap: 3px; }
    .ring-dots span {
        width: 6px; height: 6px; border-radius: 50%;
        background: var(--grey-secondary);
        animation: ring-dots 1.2s ease-in-out infinite;
    }
    .ring-dots span:nth-child(2) { animation-delay: .15s; }
    .ring-dots span:nth-child(3) { animation-delay: .3s; }
    @keyframes ring-dots {
        0%, 80%, 100% { opacity: .3; transform: scale(.8); }
        40% { opacity: 1; transform: scale(1.1); }
    }

    .body-text { flex: 1; min-width: 0; }
    .title {
        margin: 0 0 var(--space-1) 0;
        font-size: var(--text-base);
        font-weight: 500;
        color: var(--grey);
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .subtitle {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .tile-status {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
    }
    .status-detail {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .tile-footer {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        border-top: 1px solid var(--greyish);
        padding-top: var(--space-2);
    }
    .footer-note {
        font-variant-numeric: tabular-nums;
        flex: 1;
        min-width: 0;
    }

    .resume-btn {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: calc(0.3rem * 1.33);
        background: var(--surface);
        color: var(--primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-pill);
        font: inherit;
        font-size: calc(var(--text-xs) * 1.33);
        font-weight: 500;
        padding: calc(0.2rem * 1.33) calc(0.55rem * 1.33) calc(0.2rem * 1.33) calc(0.45rem * 1.33);
        cursor: pointer;
        line-height: 1.25;
        transition: background-color 0.15s, border-color 0.15s;
    }
    .resume-btn :global(i.icon) {
        margin: 0;
        font-size: calc(var(--text-xs) * 1.33);
        opacity: 0.88;
    }
    a.tile .resume-btn:hover {
        background: var(--surface-hover);
        border-color: var(--primary);
        color: var(--primary);
    }
    a.tile .resume-btn:active {
        background: var(--greyish);
    }
    a.tile .resume-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }
</style>
